import React from "react";
import type { DataSource } from "../../../../types/dataVault.types";
import type { HubEntity, LinkEntity, SatelliteEntity, TableRecord } from "../../model/types";
import { FormField } from "./FormField";
import { DataSourceEntitySelectors } from "./DataSourceEntitySelectors";
import { SubjectAreaField } from "./SubjectAreaField";
import { buildHubOptions } from "../../../../helpers/hubOptions";
import { SqlEditorField, type SqlAutocompleteContext } from "./SqlEditorField";

export function LinkMetadataForm({
  link,
  hubs,
  satellites,
  onFieldChange,
  onHubsChange,
  excludeNullBusinessKeys,
  onExcludeNullBusinessKeysChange,
  dataSources,
  dataSourcesLoading,
  dataSourcesError,
  subjectAreaOptions,
  onAddSubjectArea,
  sqlAutocomplete
}: {
  link: LinkEntity;
  hubs: HubEntity[];
  satellites: SatelliteEntity[];
  onFieldChange: (field: keyof TableRecord, value: string) => void;
  /** The connected hubs, IN THE ORDER CHOSEN. Order decides the name and the ensemble. */
  onHubsChange: (hubIds: string[]) => void;
  excludeNullBusinessKeys: boolean;
  onExcludeNullBusinessKeysChange: (next: boolean) => void;
  dataSources: DataSource[];
  dataSourcesLoading: boolean;
  dataSourcesError: string | null;
  subjectAreaOptions: string[];
  onAddSubjectArea: (value: string) => Promise<{ ok: boolean; value: string; error?: string }>;
  sqlAutocomplete?: SqlAutocompleteContext;
}): JSX.Element {
  const connectedSatelliteCount = link.satelliteIds.length;

  // One row per connected hub, plus the empty row a fresh "Add hub" click creates. Rows are
  // positional: the first is the one the name and ensemble follow.
  const hubRows = link.connectedHubIds;

  // Schema-qualified and sorted by schema then table — the model can hold hundreds of hubs, and
  // two schemas can carry the same table name.
  const hubOptions = buildHubOptions(hubs);

  const setHubAt = (index: number, hubId: string) => {
    const next = [...hubRows];
    if (hubId) {
      next[index] = hubId;
    } else {
      next.splice(index, 1);
    }
    // A hub chosen twice would ask for two identical members, which the API rejects; drop the
    // earlier occurrence so the latest choice is the one that counts.
    const deduped = next.filter((id, position) => id && next.indexOf(id) === position);
    onHubsChange(deduped);
  };

  const removeHubAt = (index: number) => {
    const next = [...hubRows];
    next.splice(index, 1);
    onHubsChange(next);
  };

  const addHubRow = () => {
    const unused = hubOptions.find((candidate) => !hubRows.includes(candidate.id));
    if (unused) {
      onHubsChange([...hubRows, unused.id]);
    }
  };

  const everyHubUsed = hubs.length > 0 && hubRows.length >= hubs.length;

  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-6">
      <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">Link metadata</h3>

      <div className="tw-mt-4 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div>
            <h4 className="tw-text-sm tw-font-semibold tw-text-slate-800">Connected hubs</h4>
            <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
              A link joins at least two. The order matters — the first hub gives the link its name,
              ensemble and subject area.
            </p>
          </div>
          <button
            type="button"
            onClick={addHubRow}
            disabled={everyHubUsed}
            title={everyHubUsed ? "Every hub in the model is already connected." : undefined}
            className="tw-shrink-0 tw-rounded-full tw-border tw-border-[#03234a] tw-bg-white tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-[#03234a] hover:tw-bg-[#03234a]/6 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            Add hub +
          </button>
        </div>

        <div className="tw-mt-3 tw-grid tw-gap-2">
          {hubRows.length === 0 ? (
            <p className="tw-rounded-xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-white tw-px-3 tw-py-3 tw-text-xs tw-text-slate-500">
              No hubs connected yet. Use <span className="tw-font-semibold">Add hub +</span>, or draw
              the relationship on the canvas.
            </p>
          ) : null}
          {hubRows.map((hubId, index) => (
            <div key={`${hubId}-${index}`} className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-w-6 tw-shrink-0 tw-text-center tw-text-[11px] tw-font-semibold tw-text-slate-500">
                {index + 1}
              </span>
              <select
                value={hubId}
                onChange={(event) => setHubAt(index, event.target.value)}
                className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-border-slate-400 focus:tw-outline-none"
              >
                <option value="">Remove this hub</option>
                {hubOptions
                  .filter((candidate) => candidate.id === hubId || !hubRows.includes(candidate.id))
                  .map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.label}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={() => removeHubAt(index)}
                aria-label={`Remove hub ${index + 1}`}
                className="tw-shrink-0 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-slate-500 hover:tw-border-rose-300 hover:tw-text-rose-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {hubRows.length === 1 ? (
          <p className="tw-mt-3 tw-text-xs tw-font-semibold tw-text-amber-700">
            A link needs at least two hubs.
          </p>
        ) : null}

        {/* Sits with the hubs because it is about THEIR keys. Off by default: switching it on
            reduces the rows a link produces, so it has to be a decision. */}
        <label className="tw-mt-4 tw-flex tw-cursor-pointer tw-items-start tw-gap-2.5 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-3">
          <input
            type="checkbox"
            checked={excludeNullBusinessKeys}
            onChange={(event) => onExcludeNullBusinessKeysChange(event.target.checked)}
            className="tw-mt-0.5"
          />
          <span>
            <span className="tw-block tw-text-xs tw-font-semibold tw-text-slate-800">
              Only create rows where every business key has a value
            </span>
            <span className="tw-mt-0.5 tw-block tw-text-[11px] tw-text-slate-500">
              Where a key is empty there is no relationship, so the row points at a hub entry that
              does not exist. Blank and whitespace count as empty. Constants are unaffected.
            </span>
          </span>
        </label>
      </div>

      <div className="tw-mt-4 tw-grid tw-gap-4">
        <FormField
          label="Target table"
          value={link.record.target_table}
          onChange={(value) => onFieldChange("target_table", value)}
          placeholder="e.g., customer__order_l"
        />
        <FormField
          label="Target schema"
          value={link.record.target_schema}
          onChange={(value) => onFieldChange("target_schema", value)}
          placeholder="e.g., rdv"
        />
        <SubjectAreaField
          value={link.record.subject_area}
          onChange={(value) => onFieldChange("subject_area", value)}
          options={subjectAreaOptions}
          onAddSubjectArea={onAddSubjectArea}
        />
        <FormField
          label="Ensemble"
          value={link.record.ensemble}
          onChange={(value) => onFieldChange("ensemble", value)}
        />
        <DataSourceEntitySelectors
          dataSources={dataSources}
          loading={dataSourcesLoading}
          error={dataSourcesError}
          selectedDataSource={link.record.data_source_name ?? ""}
          selectedSourceEntity={link.record.source_entity_name ?? ""}
          onDataSourceChange={(value, dataSource) => {
            onFieldChange("data_source_name", value);
            onFieldChange("data_source_id", dataSource ? String(dataSource.data_source_id) : "");
            onFieldChange("source_entity_name", "");
            onFieldChange("source_entity_id", "");
          }}
          onSourceEntityChange={(value, sourceEntity) => {
            onFieldChange("source_entity_name", value);
            onFieldChange(
              "source_entity_id",
              sourceEntity ? String(sourceEntity.source_entity_id) : ""
            );
          }}
        />
        <SqlEditorField
          label="Source filter"
          value={link.record.source_filter ?? ""}
          onChange={(value) => onFieldChange("source_filter", value)}
          placeholder="Enter source filter SQL"
          autocomplete={sqlAutocomplete}
        />
        <SqlEditorField
          label="DQ source filter"
          value={link.record.dq_source_filter ?? ""}
          onChange={(value) => onFieldChange("dq_source_filter", value)}
          placeholder="Enter data quality filter SQL"
          autocomplete={sqlAutocomplete}
        />
        <FormField
          label="Description"
          value={link.record.Description ?? ""}
          onChange={(value) => onFieldChange("Description", value)}
          multiline
        />
      </div>
      <div className="tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-slate-50 tw-p-3 tw-text-xs tw-text-slate-600">
        <span>Connected satellites: {connectedSatelliteCount}</span>
        <span>Total hubs in model: {hubs.length}</span>
        <span>Total satellites in model: {satellites.length}</span>
      </div>
    </section>
  );
}
